import { useState } from "react";
import { Link } from "react-router-dom";

export default function ForgotPassword(): JSX.Element {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim() }),
      });
      // The endpoint answers 200 for both a known and an unknown address, so a
      // 200 is the only outcome that means the link is on its way. Showing
      // "check your email" for a 429 or a 500 tells the user to wait for mail
      // that was never sent — on the one flow they reach when already locked
      // out. Enumeration safety is unaffected: this branches on transport
      // status, which is identical for every address.
      if (!res.ok) {
        setError(
          res.status === 429
            ? "Too many requests. Please wait a few minutes and try again."
            : "We couldn't send the reset link just now. Please try again.",
        );
        return;
      }
      setSent(true);
    } catch {
      setError("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <main className="min-h-screen grid place-items-center p-8">
        <div className="max-w-sm w-full text-center">
          <h1 className="font-display text-2xl font-extrabold mb-2">Check your email</h1>
          <p className="text-muted-foreground mb-6">
            If that email is registered, a password reset link has been sent.
          </p>
          <Link to="/signin" className="text-primary underline text-sm">Back to sign in</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen grid place-items-center p-8">
      <div className="max-w-sm w-full">
        <h1 className="font-display text-2xl font-extrabold mb-2 text-center">Reset your password</h1>
        <p className="text-center text-sm text-muted-foreground mb-6">
          Enter your email and we'll send a reset link.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className="text-sm font-medium">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-control border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm font-medium text-destructive">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="rounded-control bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
        <p className="mt-4 text-center text-sm">
          <Link to="/signin" className="text-primary underline">Back to sign in</Link>
        </p>
      </div>
    </main>
  );
}
